import React, { FC, useEffect, useState } from "react";
import styles from "./AnimationExplorerCard.module.scss";
import { useMe, useCurrentOrganization } from "@/Hooks";
import { useGetSavedAnimationsQuery } from "@/features/auth/api/authAPI";
import { handleOpenVideoPage } from "@/app/libs/const/handleOpenVideoPage";
import { useSelectedAnimationScreens } from "@/entities/selectedScreens";
import { VideoLinkProvider } from "@/app/providers/video/VideoLinkProvider";

interface AnimationExplorerCardProps {
  name: string;
  shortname: string;
  icon: string;
  video: string;
  time_from: string;
  id: number;
  app_video: number;
  slug: string;
  appid: number;
  avpk: number;
  is_saved_pk: number;
  is_saved?: boolean;
}

const AnimationExplorerCard: FC<AnimationExplorerCardProps> = ({
  name,
  shortname,
  icon,
  video,
  time_from,
  app_video,
  id,
  slug,
  appid,
  avpk,
  is_saved_pk,
  is_saved: propIsSaved,
}) => {
  const { addScreen, removeScreen, isSelected, selectedScreens } =
    useSelectedAnimationScreens();

  const [selected, setSelected] = useState(false);

  const { me } = useMe();
  const currentOrganization = useCurrentOrganization();

  const [isPlaying, setisPlaying] = useState(false);

  // Get saved animations for current organization only if we have a valid UUID
  const { data: savedAnimations } = useGetSavedAnimationsQuery(
    { organizationUuid: currentOrganization?.uuid || "" },
    { skip: !currentOrganization?.uuid },
  );

  // Check if this animation is saved - use prop value if provided
  const isSaved =
    propIsSaved !== undefined
      ? propIsSaved
      : !!savedAnimations?.results.find(
          (saved) => saved.app_video_animation.id === id,
        );

  const redirectToVideoPage = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    handleOpenVideoPage(
      +time_from,
      slug,
      "Home Page screens",
      1,
      appid,
      app_video,
      undefined,
      me?.username || "",
      currentOrganization?.uuid || "",
    );
  };

  const playVideoOnHover = (e: React.MouseEvent<HTMLVideoElement>) => {
    const promise = e.currentTarget.play();
    if (promise !== undefined) {
      promise
        .then(() => {
          setisPlaying(true);
        })
        .catch(() => {
          setisPlaying(false);
        });
    }
  };

  const pauseVideoOnHover = (e: React.MouseEvent<HTMLVideoElement>) => {
    if (isPlaying) {
      e.currentTarget.pause();
      setisPlaying(false);
    }
  };

  const handleSelectAnimation = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (selected) {
      removeScreen(id);
      setSelected(false);
    } else {
      addScreen({
        id: id,
        app_video: app_video,
        is_saved: isSaved,
        is_saved_pk: is_saved_pk,
      });
      setSelected(true);
    }
  };

  useEffect(() => {
    setSelected(isSelected(id));
  }, [selectedScreens, id, isSelected]);

  return (
    <div className={styles.card}>
      <VideoLinkProvider
        timestamp={+time_from}
        slugText={slug}
        from="Animation Explorer Page"
        videoType={1}
        appId={appid}
        videoId={app_video}
        userName={me?.username || ""}
        organizationId={currentOrganization?.uuid || ""}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className={styles.card__header}>
          <div className={styles.card__header__icon}>
            <img src={icon} alt={name} />
          </div>
          <div className={styles.card__header__title}>
            <h1 className={styles.card__header__title__name}>{name}</h1>
            <p>{shortname}</p>
          </div>
        </div>
      </VideoLinkProvider>
      <div className={styles.card__video_container}>
        <div className={styles.card__video}>
          <video
            src={video}
            preload="auto"
            muted
            loop
            onMouseOver={playVideoOnHover}
            onMouseOut={pauseVideoOnHover}
          />
        </div>
        <div
          className={`${styles.selectButtonContainer} ${selected ? styles.alwaysVisible : ""}`}
        >
          <div className={styles.selectButtonContainerInner}>
            {isSaved && (
              <div className={styles.savedTag}>
                <p>Saved</p>
              </div>
            )}

            <button
              className={`${styles.selectButton} ${selected ? styles.selected : ""}`}
              onClick={handleSelectAnimation}
            >
              {selected && (
                <img src="/img/blackIcon/Check.svg" alt="selectImage" />
              )}
            </button>
          </div>
        </div>
        <div className={styles.bottomMenuContainer}>
          <div className={styles.bottomMenuContainerInner}>
            <VideoLinkProvider
              timestamp={+time_from}
              slugText={slug}
              from="Animation Explorer Page"
              videoType={1}
              appId={appid}
              videoId={app_video}
              userName={me?.username || ""}
              organizationId={currentOrganization?.uuid || ""}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <button>
                <img src="/img/see-video-icon.svg" alt="see-video" />
                <p>See Video</p>
              </button>
            </VideoLinkProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimationExplorerCard;
